import React from 'react';
import { get } from 'lodash';
import moment from 'moment';
import { useAtomValue } from 'jotai';
import { percentileAtom } from '@components/metrics-panel-hz/atoms';
import { percentileConfig } from '@src/utils/metrics';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { buildDimensionQuery } from './utils';
import { getDimensionData } from '@api/dimensionContainer';
import instance from '@api/axiosInstance';
import getQueryTag from '@utils/misc/queryTag';
import { checkIfSampledCluster } from '@components/common/SampledDataWarning';
import { isAppModule } from '@src/utils/constants';
import { addLocalPercentageMetrics } from '@src/utils/table/parseUtils';
import { processAnnotations } from '@src/components/common/DataAnnotations';
import { useCurrentKpiConfig } from '@src/hooks/useCurrentKpiConfig';
import { useAiAlertDiagnosticsKpiConfig } from '@src/hooks/useAiAlertDiagnosticsKpiConfig';

interface UseFetchGroupByProps {
  dimension: string;
  metrics: string[];
  filter: any;
  interval: { from: string; to: string };
  metricFilter?: string[] | null;
  enabled?: boolean;
  sortByMetric?: string;
  limit?: number;
  realTimeOnTrends?: boolean;
  noBrushSelected?: boolean;
  comparisonInterval?: { from: string; to: string } | null;
  isTableCompareEnabled?: boolean;
  viewId?: string;
  withTotals?: boolean;
  doNotSort?: boolean;
}

export function flattenData(res, query) {
  const { data: _data, totals: _totals } = get(res, ['0'], {});
  const totals = get(_totals, ['0', 'results']);
  const rows = get(_data, ['0', 'results']);

  const adjustedInterval = get(
    res,
    '[0].annotations[0].value',
    `${moment().format()}/${moment().format()}`,
  );
  addLocalPercentageMetrics(query.metrics, rows, totals);
  return {
    totals,
    rows,
    adjustedInterval,
    annotations: get(res, '[0].annotations'),
  };
}

function useFetchGroupBy({
  dimension,
  metrics,
  filter,
  interval,
  metricFilter = null,
  enabled = true,
  sortByMetric,
  limit = 200,
  realTimeOnTrends,
  noBrushSelected = false,
  comparisonInterval = null,
  isTableCompareEnabled = false,
  viewId,
  withTotals,
  doNotSort = false,
}: UseFetchGroupByProps) {
  const percentile = useAtomValue(percentileAtom);
  const { eventId, dataScope, usecaseKey }: any = useSelector(activeDashboardSelector);
  const currentKpiConfig = useCurrentKpiConfig();
  // On the AI-alert diagnostics page, use the alert's own KPI config (from its meta) so
  // KPI-based % metrics are computed against the thresholds that produced the alert.
  const diagnosticsKpiConfig = useAiAlertDiagnosticsKpiConfig();
  const { _rKey = 0, rule } = filter;
  const query: any = buildDimensionQuery({
    dimension,
    metrics,
    filter: rule,
    interval: `${moment(interval.from).format()}/${moment(interval.to).format()}`,
    sortByMetric,
    doNotSort,
  });

  const comparisonIntervalString =
    comparisonInterval &&
    `${moment(comparisonInterval.from).format()}/${moment(comparisonInterval.to).format()}`;
  query.limit = limit;
  query.isLiveMode = realTimeOnTrends;
  query._rkey = _rKey;

  if (realTimeOnTrends && noBrushSelected) {
    query.ignoreInterval = true;
  }

  if (isTableCompareEnabled || (metricFilter && metricFilter.length > 0)) {
    query.metricFilter = metricFilter;
    query.type = 'multi-group-by';
    query.comparisonInterval = isTableCompareEnabled && comparisonIntervalString;
  }

  if (isAppModule) {
    if (
      percentile &&
      percentileConfig[percentile] &&
      percentileConfig[percentile].backendPercentile
    ) {
      query.options.percentile = percentileConfig[percentile].backendPercentile;
    }
    query.options.dataScope = dataScope;
    query.options.presetKey = usecaseKey;
    if (eventId) {
      query.options.eventId = eventId;
    }
    query.options.viewId = viewId;
  }
  query.options.kpiConfig = diagnosticsKpiConfig ?? currentKpiConfig;

  if (withTotals !== undefined) {
    query.options.withTotals = withTotals;
  }
  const queryTag = getQueryTag({ liveMode: realTimeOnTrends });
  return useQuery(
    ['dimension-groupby', query],
    () => {
      return getDimensionData({ queries: [query] }, { headers: { ...queryTag } }, instance).then(
        (response) => {
          // TODO: move annotation parsing to common util - processAnnotations
          const annotations = get(response, ['data', 0, 'annotations'], []);
          checkIfSampledCluster(annotations);
          processAnnotations(annotations);

          if (query.type === 'multi-group-by') {
            const { metadataFilterData, metricFilterData } = get(response, ['data'], {});
            return {
              metadataFilterData: flattenData(metadataFilterData, query),
              metricFilterData: flattenData(metricFilterData, query),
            };
          } else {
            return flattenData(response.data, query);
          }
        },
      );
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && metrics.length > 0 && !!sortByMetric,
      keepPreviousData: realTimeOnTrends, // loading is disable on RT mode, enable to avoid showing no data layer
    },
  );
}

export default useFetchGroupBy;
